import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RotateCw, Activity, ListChecks, BarChart3 } from "lucide-react";

const BASE_URL = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");

interface Summary {
  errorEvents24h: number;
  errorEvents7d: number;
  requests24h: number;
  requestErrors24h: number;
}

interface ErrorRow {
  id: number;
  occurredAt: string;
  source: string;
  level: string;
  message: string;
  stack: string | null;
  route: string | null;
  statusCode: number | null;
  context: Record<string, unknown> | null;
}

interface TopError {
  fingerprint: string | null;
  source: string;
  message: string;
  count: number;
  last_seen: string;
}

interface MetricRow {
  name: string;
  count: number;
  mean: number;
  p95: number;
  error_count: number;
}

interface RouteRow {
  route: string;
  count: number;
  mean_ms: number;
  p95_ms: number;
  errors: number;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

function formatMs(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n < 1) return "<1 ms";
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(2)} s`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

export default function ObservabilityPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [errors, setErrors] = useState<ErrorRow[]>([]);
  const [topErrors, setTopErrors] = useState<TopError[]>([]);
  const [metrics, setMetrics] = useState<MetricRow[]>([]);
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const errorPath = sourceFilter
        ? `/api/admin/observability/errors?source=${encodeURIComponent(sourceFilter)}`
        : `/api/admin/observability/errors`;
      const [s, e, t, m, r] = await Promise.all([
        fetchJson<Summary>("/api/admin/observability/summary"),
        fetchJson<{ errors: ErrorRow[] }>(errorPath),
        fetchJson<{ topErrors: TopError[] }>("/api/admin/observability/top-errors"),
        fetchJson<{ metrics: MetricRow[] }>("/api/admin/observability/metrics"),
        fetchJson<{ routes: RouteRow[] }>("/api/admin/observability/route-metrics"),
      ]);
      setSummary(s);
      setErrors(e.errors);
      setTopErrors(t.topErrors);
      setMetrics(m.metrics);
      setRoutes(r.routes);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sourceFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl font-serif font-bold text-foreground">Observability</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Errors, request latency, and integration health.
            </p>
          </div>
          <Button variant="outline" onClick={refresh} disabled={loading} className="gap-2">
            <RotateCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 text-destructive p-4 text-sm">
            Failed to load: {error}
          </div>
        )}

        {/* ── Summary cards ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            icon={<AlertTriangle className="w-4 h-4 text-destructive" />}
            title="Errors (24h)"
            value={summary?.errorEvents24h ?? "—"}
            subtle={`${summary?.errorEvents7d ?? "—"} in 7d`}
          />
          <SummaryCard
            icon={<Activity className="w-4 h-4 text-primary" />}
            title="Requests (24h)"
            value={summary?.requests24h ?? "—"}
          />
          <SummaryCard
            icon={<ListChecks className="w-4 h-4 text-orange-500" />}
            title="HTTP errors (24h)"
            value={summary?.requestErrors24h ?? "—"}
            subtle={
              summary && summary.requests24h > 0
                ? `${((summary.requestErrors24h / summary.requests24h) * 100).toFixed(1)}% error rate`
                : ""
            }
          />
          <SummaryCard
            icon={<BarChart3 className="w-4 h-4 text-emerald-500" />}
            title="Tracked metrics"
            value={metrics.length}
            subtle={`${routes.length} active routes`}
          />
        </div>

        {/* ── Integration health metrics ─────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Integration health (24h)</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {metrics.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No metrics recorded yet. They appear after the first AI extraction, nutrition lookup, or storage operation.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Operation</th>
                    <th className="py-2 pr-4 font-medium text-right">Calls</th>
                    <th className="py-2 pr-4 font-medium text-right">Mean</th>
                    <th className="py-2 pr-4 font-medium text-right">p95</th>
                    <th className="py-2 pr-4 font-medium text-right">Errors</th>
                    <th className="py-2 pr-4 font-medium text-right">Failure rate</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((m) => {
                    const rate = m.count > 0 ? (m.error_count / m.count) * 100 : 0;
                    return (
                      <tr key={m.name} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-mono text-xs">{m.name.replace(/\.latency_ms$/, "")}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{m.count}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{formatMs(m.mean)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{formatMs(m.p95)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{m.error_count}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          <span className={rate > 5 ? "text-destructive font-medium" : ""}>
                            {rate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* ── Per-route HTTP metrics ─────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top routes by request volume (24h)</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {routes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No route metrics yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Route</th>
                    <th className="py-2 pr-4 font-medium text-right">Requests</th>
                    <th className="py-2 pr-4 font-medium text-right">Mean</th>
                    <th className="py-2 pr-4 font-medium text-right">p95</th>
                    <th className="py-2 pr-4 font-medium text-right">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((r) => (
                    <tr key={r.route} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{r.route}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{r.count}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{formatMs(r.mean_ms)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{formatMs(r.p95_ms)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        <span className={r.errors > 0 ? "text-destructive font-medium" : ""}>
                          {r.errors}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* ── Top errors by frequency ────────────────────────────────────── */}
        {topErrors.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Top errors (7d, by frequency)</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Source</th>
                    <th className="py-2 pr-4 font-medium">Message</th>
                    <th className="py-2 pr-4 font-medium text-right">Count</th>
                    <th className="py-2 pr-4 font-medium text-right">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {topErrors.map((t) => (
                    <tr key={`${t.fingerprint}-${t.source}`} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="text-xs">{t.source}</Badge>
                      </td>
                      <td className="py-2 pr-4 max-w-md truncate">{t.message}</td>
                      <td className="py-2 pr-4 text-right tabular-nums font-medium">{t.count}</td>
                      <td className="py-2 pr-4 text-right text-muted-foreground tabular-nums">
                        {formatRelative(t.last_seen)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* ── Recent errors (full detail, click to expand stack) ─────────── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent error events</CardTitle>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="text-sm border rounded-md px-2 py-1 bg-background"
              >
                <option value="">All sources</option>
                <option value="api">API</option>
                <option value="client">Client</option>
                <option value="process">Process</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {errors.length === 0 ? (
              <p className="text-sm text-muted-foreground">No errors recorded in the last 7 days. Nice.</p>
            ) : (
              <div className="space-y-2">
                {errors.map((e) => (
                  <div key={e.id} className="border rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <Badge variant="outline" className="text-xs">{e.source}</Badge>
                        <Badge
                          variant="outline"
                          className={`text-xs ${e.level === "fatal" ? "border-destructive text-destructive" : ""}`}
                        >
                          {e.level}
                        </Badge>
                        {e.statusCode != null && (
                          <Badge variant="outline" className="text-xs">{e.statusCode}</Badge>
                        )}
                        {e.route && (
                          <span className="text-xs font-mono text-muted-foreground">{e.route}</span>
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                          {formatRelative(e.occurredAt)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm truncate">{e.message}</div>
                    </button>
                    {expandedId === e.id && (
                      <div className="px-3 py-2 border-t bg-muted/20 text-xs space-y-2">
                        {e.stack && (
                          <pre className="whitespace-pre-wrap font-mono overflow-x-auto max-h-64">
                            {e.stack}
                          </pre>
                        )}
                        {e.context && Object.keys(e.context).length > 0 && (
                          <div>
                            <div className="font-medium text-muted-foreground mb-1">Context</div>
                            <pre className="whitespace-pre-wrap font-mono overflow-x-auto max-h-40">
                              {JSON.stringify(e.context, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function SummaryCard({
  icon,
  title,
  value,
  subtle,
}: {
  icon: React.ReactNode;
  title: string;
  value: number | string;
  subtle?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
          {icon}
          {title}
        </div>
        <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
        {subtle && <div className="text-xs text-muted-foreground mt-1">{subtle}</div>}
      </CardContent>
    </Card>
  );
}
